import { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Sheet, SheetBody, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Download,
  Loader2,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react'
import type { Order, User, VoidLogEntry, VoidLogSummary } from '@/types'
import { useCurrency } from '@/contexts/CurrencyContext'
import { useDayBoundaryHour } from '@/hooks/useDayBoundaryHour'
import { cn, formatDateHuman, formatDateTimeHuman, formatTimeHuman } from '@/lib/utils'
import { compactCurrency } from '@/components/admin/dashboard/dashboardFormat'
import { ALL_VOID_REASON_LABELS, VOID_OUTCOME_LABEL } from '@/lib/void-reasons'
import { useDashboardStream } from '@/lib/dashboardStream'
import { toastHelpers } from '@/lib/toast-helpers'
import { LiveStatusPill } from '@/components/ui/live-status-pill'
import { useReportRange } from '@/hooks/useReportRange'
import { DateRangeFilter } from '@/components/admin/reports/DateRangeFilter'
import {
  daysAfterLabel,
  isSelfAuthorized,
  orderTypeLabel,
  splitReason,
  voidValue,
} from '@/lib/voidLog'

const NONE = '__all__'
const PER_PAGE_OPTIONS = [20, 50, 100]
const EXPORT_HARD_CAP = 5000

// CSV export — columns are stable; downstream spreadsheets key off them.
function csvEscape(value: string | number | undefined | null): string {
  const s = value === undefined || value === null ? '' : String(value)
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}

function downloadBlob(rows: string[], filename: string): void {
  const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

const CSV_HEADER = [
  'created_at',
  'order_number',
  'item_name',
  'quantity',
  'unit_price',
  'value',
  'reason',
  'outcome',
  'waste_cost',
  'voided_by',
  'authorized_by',
]

function entryToCsvRow(e: VoidLogEntry): string {
  return [
    csvEscape(e.created_at),
    csvEscape(e.order_number ?? ''),
    csvEscape(e.item_name),
    csvEscape(e.quantity),
    csvEscape(e.unit_price),
    csvEscape(voidValue(e)),
    csvEscape(e.reason ?? ''),
    csvEscape(e.outcome ? VOID_OUTCOME_LABEL[e.outcome] : ''),
    csvEscape(e.waste_cost ?? ''),
    csvEscape(e.voided_by_name ?? ''),
    csvEscape(e.authorized_name ?? ''),
  ].join(',')
}

type Drill = { orderId: string; voidId: string } | null

export function VoidLog() {
  const { formatCurrency } = useCurrency()
  // F-FE-N148: "n days after the order" counts business days, so a tenant
  // trading past midnight is not shown voiding bills a day late.
  const boundaryHour = useDayBoundaryHour()
  const queryClient = useQueryClient()

  // Pagination
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(20)
  const [pageInput, setPageInput] = useState('')

  // Filters. The period is the one filter always on screen; the rest live
  // behind "More filters" and surface as chips once applied.
  const range = useReportRange('last_30')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [voidedBy, setVoidedBy] = useState<string>(NONE)
  const [authorizedBy, setAuthorizedBy] = useState<string>(NONE)
  const [reason, setReason] = useState<string>(NONE)
  // F-CTRL-N74: not made / made and thrown away / from before either existed.
  const [outcome, setOutcome] = useState<string>(NONE)
  const [minValue, setMinValue] = useState('')
  const [moreOpen, setMoreOpen] = useState(false)

  const [drill, setDrill] = useState<Drill>(null)
  const [exporting, setExporting] = useState(false)

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim())
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [search])

  // A new period always starts from page 1.
  useEffect(() => {
    setPage(1)
  }, [range.fromISO, range.toISO])

  const minValueNumber = useMemo(() => {
    const n = parseFloat(minValue)
    return Number.isFinite(n) && n > 0 ? n : undefined
  }, [minValue])

  const filterParams = useMemo(
    () => ({
      from: range.fromISO,
      to: range.toISO,
      voided_by: voidedBy === NONE ? undefined : voidedBy,
      authorized_by: authorizedBy === NONE ? undefined : authorizedBy,
      reason: reason === NONE ? undefined : reason,
      outcome: outcome === NONE ? undefined : outcome,
      q: debouncedSearch || undefined,
      min_value: minValueNumber,
    }),
    [range.fromISO, range.toISO, voidedBy, authorizedBy, reason, outcome, debouncedSearch, minValueNumber],
  )
  const listParams = useMemo(
    () => ({ ...filterParams, page, per_page: perPage }),
    [filterParams, page, perPage],
  )

  // Both keys start with 'void-log' so one invalidation refreshes both.
  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['void-log', 'list', listParams],
    queryFn: () => apiClient.getVoidLog(listParams),
  })
  const { data: summaryRes, isLoading: summaryLoading } = useQuery({
    queryKey: ['void-log', 'summary', filterParams],
    queryFn: () => apiClient.getVoidLogSummary(filterParams),
  })
  const summary: VoidLogSummary | undefined = summaryRes?.data

  // Staff lookup for the filter dropdowns. Cached separately.
  const { data: staffData } = useQuery({
    queryKey: ['void-log:staff'],
    queryFn: () => apiClient.getUsers({ per_page: 100 }),
    staleTime: 5 * 60 * 1000,
  })
  const staffList: User[] = useMemo(() => {
    const raw = staffData?.data as unknown
    if (Array.isArray(raw)) return raw as User[]
    if (raw && typeof raw === 'object' && Array.isArray((raw as any).data)) {
      return (raw as any).data as User[]
    }
    return []
  }, [staffData])
  const voiders = useMemo(
    () => staffList.filter((u) => u.is_active && ['counter', 'manager', 'admin'].includes(u.role)),
    [staffList],
  )
  const authorizers = useMemo(
    () => staffList.filter((u) => u.is_active && ['admin', 'manager'].includes(u.role)),
    [staffList],
  )
  const staffName = (id: string) => {
    const u = staffList.find((s) => s.id === id)
    return u ? `${u.first_name} ${u.last_name}` : 'Staff member'
  }

  const entries = (data?.data as VoidLogEntry[] | null) || []
  const meta = data?.meta || { current_page: 1, total_pages: 1, total: 0, per_page: perPage }

  // Live updates — the admin dashboard SSE emits `order_voided` from the same
  // handler that writes void_log. (Not /kitchen/stream: that is gated on KDS
  // and would show managers permanently "offline".)
  const streamStatus = useDashboardStream({
    enabled: true,
    onEvent: (ev) => {
      if (ev.type === 'order_voided') {
        queryClient.invalidateQueries({ queryKey: ['void-log'] })
      }
    },
  })

  // Chips describe the filters hidden in the popover.
  const chips: { key: string; label: string; clear: () => void }[] = []
  if (voidedBy !== NONE) chips.push({ key: 'voided_by', label: `Voided by ${staffName(voidedBy)}`, clear: () => setVoidedBy(NONE) })
  if (authorizedBy !== NONE) chips.push({ key: 'authorized_by', label: `Approved by ${staffName(authorizedBy)}`, clear: () => setAuthorizedBy(NONE) })
  if (reason !== NONE) chips.push({ key: 'reason', label: `Reason: ${reason}`, clear: () => setReason(NONE) })
  if (outcome !== NONE) chips.push({ key: 'outcome', label: outcomeFilterLabel(outcome), clear: () => setOutcome(NONE) })
  if (minValueNumber) chips.push({ key: 'min', label: `At least ${formatCurrency(minValueNumber)}`, clear: () => setMinValue('') })

  const hasActiveFilters = chips.length > 0 || !!search
  const clearAllFilters = () => {
    setVoidedBy(NONE)
    setAuthorizedBy(NONE)
    setReason(NONE)
    setOutcome(NONE)
    setMinValue('')
    setSearch('')
    setPage(1)
  }
  const onFilterChange = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v)
    setPage(1)
  }

  // Drill-down: the order plus every void on it. Fires only when a row opens.
  const { data: drillOrder, isLoading: drillLoading } = useQuery({
    queryKey: ['void-log:order', drill?.orderId],
    queryFn: () => apiClient.getOrder(drill!.orderId),
    enabled: !!drill,
  })
  const orderDetail: Order | undefined = drillOrder?.data
  const { data: drillVoids } = useQuery({
    queryKey: ['void-log:order-voids', drill?.orderId],
    queryFn: () => apiClient.getVoidLog({ per_page: 100, order_number: orderDetail?.order_number }),
    enabled: !!drill && !!orderDetail?.order_number,
  })
  const orderVoids = useMemo(() => {
    const list = (drillVoids?.data as VoidLogEntry[] | null) || []
    const own = list.filter((v) => v.order_id === drill?.orderId)
    // The void the manager clicked reads first.
    return [...own].sort((a, b) => (a.id === drill?.voidId ? -1 : b.id === drill?.voidId ? 1 : 0))
  }, [drillVoids, drill])

  // CSV exports ------------------------------------------------------------
  const exportCurrentPage = () => {
    if (entries.length === 0) {
      toastHelpers.warning('Nothing to export', 'There are no rows on this page.')
      return
    }
    const lines = [CSV_HEADER.join(','), ...entries.map(entryToCsvRow)]
    downloadBlob(lines, `void-log-page-${meta.current_page}-${new Date().toISOString().slice(0, 10)}.csv`)
  }

  const exportAllFiltered = async () => {
    if (meta.total === 0) {
      toastHelpers.warning('Nothing to export', 'No voids match the current filters.')
      return
    }
    if (meta.total > EXPORT_HARD_CAP) {
      toastHelpers.warning(
        'Too many rows',
        `Narrow the period or filters — exports are capped at ${EXPORT_HARD_CAP.toLocaleString()} rows.`,
      )
      return
    }
    setExporting(true)
    try {
      // Backend caps per_page at 100.
      const pageSize = 100
      const totalPages = Math.ceil(meta.total / pageSize)
      const all: VoidLogEntry[] = []
      for (let p = 1; p <= totalPages; p++) {
        const res = await apiClient.getVoidLog({ ...filterParams, page: p, per_page: pageSize })
        const rows = (res.data as VoidLogEntry[] | null) || []
        all.push(...rows)
        if (rows.length === 0) break
      }
      const lines = [CSV_HEADER.join(','), ...all.map(entryToCsvRow)]
      downloadBlob(lines, `void-log-${range.fromISO}-to-${range.toISO}.csv`)
      toastHelpers.success('Export ready', `${all.length.toLocaleString()} rows exported.`)
    } catch (err) {
      toastHelpers.apiError('Export void log', err as Error)
    } finally {
      setExporting(false)
    }
  }

  const openRow = (entry: VoidLogEntry) => {
    if (!entry.order_id) return
    setDrill({ orderId: entry.order_id, voidId: entry.id })
  }

  // ---- Render ------------------------------------------------------------
  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <h2 className="text-3xl font-bold tracking-tight">Void Log</h2>
            <LiveStatusPill status={streamStatus} />
          </div>
          <p className="text-muted-foreground mt-1">
            Every item voided after it was fired to the kitchen, with who did it and why
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" disabled={exporting}>
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={exportCurrentPage} disabled={entries.length === 0}>
              Export this page
            </DropdownMenuItem>
            <DropdownMenuItem onClick={exportAllFiltered} disabled={meta.total === 0}>
              Export all filtered ({meta.total.toLocaleString()} rows)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <DateRangeFilter range={range} />
        <div className="relative min-w-[220px] flex-1 sm:flex-none sm:w-64">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Order number or item"
            aria-label="Search by order number or item"
            className="pl-8 h-9"
          />
        </div>
        <Popover open={moreOpen} onOpenChange={setMoreOpen}>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm" className="h-9">
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              More filters
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-80 space-y-4">
            <FilterField label="Voided by">
              <Select value={voidedBy} onValueChange={onFilterChange(setVoidedBy)}>
                <SelectTrigger><SelectValue placeholder="Anyone" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Anyone</SelectItem>
                  {voiders.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.first_name} {u.last_name}
                      <span className="text-muted-foreground ml-1 text-xs">({u.role})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterField>
            <FilterField label="Approved by">
              <Select value={authorizedBy} onValueChange={onFilterChange(setAuthorizedBy)}>
                <SelectTrigger><SelectValue placeholder="Anyone" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Anyone</SelectItem>
                  {authorizers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.first_name} {u.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterField>
            <FilterField label="Was it made?">
              <Select value={outcome} onValueChange={onFilterChange(setOutcome)}>
                <SelectTrigger><SelectValue placeholder="Either" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Either</SelectItem>
                  <SelectItem value="not_made">{VOID_OUTCOME_LABEL.not_made}</SelectItem>
                  <SelectItem value="wasted">{VOID_OUTCOME_LABEL.wasted}</SelectItem>
                  <SelectItem value="unclassified">Not recorded (older voids)</SelectItem>
                </SelectContent>
              </Select>
            </FilterField>
            <FilterField label="Reason">
              <Select value={reason} onValueChange={onFilterChange(setReason)}>
                <SelectTrigger><SelectValue placeholder="Any reason" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Any reason</SelectItem>
                  {ALL_VOID_REASON_LABELS.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterField>
            <FilterField label="Minimum value">
              <Input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                placeholder="0"
                value={minValue}
                onChange={(e) => onFilterChange(setMinValue)(e.target.value)}
              />
            </FilterField>
            <div className="flex justify-between pt-1">
              <Button variant="ghost" size="sm" onClick={clearAllFilters} disabled={!hasActiveFilters}>
                Clear
              </Button>
              <Button size="sm" onClick={() => setMoreOpen(false)}>Done</Button>
            </div>
          </PopoverContent>
        </Popover>

        {chips.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => { c.clear(); setPage(1) }}
            className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2.5 py-1 text-xs font-medium hover:bg-primary/15 focus:outline-none focus:ring-2 focus:ring-ring"
            aria-label={`Remove filter ${c.label}`}
          >
            {c.label}
            <X className="w-3 h-3" />
          </button>
        ))}

        <span className="ml-auto text-sm text-muted-foreground tabular-nums">
          {isFetching ? 'Updating…' : `${meta.total.toLocaleString()} void${meta.total === 1 ? '' : 's'}`}
        </span>
      </div>

      {/* Summary strip */}
      {(summaryLoading || (summary && summary.count > 0)) && (
        <SummaryStrip summary={summary} loading={summaryLoading} formatCurrency={formatCurrency} />
      )}

      {/* Table */}
      <Card>
        <CardContent className="pt-0">
          <div className="overflow-x-auto [-webkit-overflow-scrolling:touch]">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="border-b">
                  <Th>When</Th>
                  <Th>Item</Th>
                  <Th className="text-right">Lost</Th>
                  <Th>Reason</Th>
                  <Th>Voided by</Th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-muted-foreground">
                      <Loader2 className="w-5 h-5 inline animate-spin mr-2" />
                      Loading void log…
                    </td>
                  </tr>
                ) : isError ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center">
                      <div className="text-red-600 font-medium">Failed to load void log</div>
                      <div className="text-sm text-muted-foreground mt-1">
                        {(error as Error)?.message || 'Unexpected error'}
                      </div>
                      <Button size="sm" variant="outline" className="mt-3" onClick={() => refetch()}>
                        Retry
                      </Button>
                    </td>
                  </tr>
                ) : entries.length === 0 ? (
                  <EmptyState hasActiveFilters={hasActiveFilters} onClear={clearAllFilters} />
                ) : (
                  entries.map((entry) => {
                    const { category, note } = splitReason(entry.reason)
                    const self = isSelfAuthorized(entry)
                    const clickable = !!entry.order_id
                    const selected = drill?.voidId === entry.id
                    const sub = [
                      entry.order_number,
                      orderTypeLabel(entry.order_type),
                      entry.table_number ? `Table ${entry.table_number}` : null,
                    ].filter(Boolean).join(' · ')
                    return (
                      <tr
                        key={entry.id}
                        role={clickable ? 'button' : undefined}
                        tabIndex={clickable ? 0 : undefined}
                        aria-label={clickable ? `Open order ${entry.order_number ?? ''}` : undefined}
                        onClick={() => openRow(entry)}
                        onKeyDown={(e) => {
                          if (clickable && (e.key === 'Enter' || e.key === ' ')) {
                            e.preventDefault()
                            openRow(entry)
                          }
                        }}
                        className={cn(
                          'border-b align-top transition-colors',
                          clickable && 'cursor-pointer hover:bg-muted/50 focus:outline-none focus-visible:bg-muted/60',
                          selected && 'bg-accent hover:bg-accent',
                        )}
                      >
                        <td className="p-3 whitespace-nowrap">
                          <div>{formatDateHuman(entry.created_at)}</div>
                          <div className="text-xs text-muted-foreground">{formatTimeHuman(entry.created_at)}</div>
                        </td>
                        <td className="p-3">
                          <div className="font-medium">
                            {entry.item_name}
                            <span className="text-muted-foreground font-normal ml-1">×{entry.quantity}</span>
                          </div>
                          {sub && <div className="text-xs text-muted-foreground font-mono mt-0.5">{sub}</div>}
                        </td>
                        <td className="p-3 text-right tabular-nums font-semibold text-red-600 whitespace-nowrap">
                          {formatCurrency(voidValue(entry))}
                          {entry.outcome === 'wasted' && entry.waste_cost != null && (
                            <div className="text-xs font-normal text-muted-foreground">food {formatCurrency(entry.waste_cost)}</div>
                          )}
                        </td>
                        <td className="p-3">
                          <div>{category}</div>
                          {note && <div className="text-xs text-muted-foreground mt-0.5 break-words max-w-[16rem]">{note}</div>}
                          {entry.outcome && <OutcomePill outcome={entry.outcome} />}
                        </td>
                        <td className="p-3">
                          <div>{entry.voided_by_name || '—'}</div>
                          {self ? (
                            <SelfAuthorizedMark />
                          ) : entry.authorized_name ? (
                            <div className="text-xs text-muted-foreground mt-0.5">approved by {entry.authorized_name}</div>
                          ) : null}
                        </td>
                        <td className="p-3 text-right text-muted-foreground">
                          {clickable && <ChevronRight className="w-4 h-4 inline" />}
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {(meta.total_pages > 1 || entries.length > 0) && (
            <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t mt-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>Rows per page</span>
                <Select
                  value={String(perPage)}
                  onValueChange={(v) => {
                    setPerPage(Number(v))
                    setPage(1)
                  }}
                >
                  <SelectTrigger className="h-8 w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PER_PAGE_OPTIONS.map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(1)} aria-label="First page">
                  <ChevronsLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} aria-label="Previous page">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-muted-foreground px-1">
                  Page <strong>{meta.current_page}</strong> of {meta.total_pages || 1}
                </span>
                <Button variant="outline" size="sm" disabled={page >= meta.total_pages} onClick={() => setPage((p) => p + 1)} aria-label="Next page">
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={page >= meta.total_pages} onClick={() => setPage(meta.total_pages)} aria-label="Last page">
                  <ChevronsRight className="w-4 h-4" />
                </Button>
                <form
                  className="flex items-center gap-1 ml-2"
                  onSubmit={(e) => {
                    e.preventDefault()
                    const n = parseInt(pageInput, 10)
                    if (Number.isFinite(n) && n >= 1 && n <= meta.total_pages) {
                      setPage(n)
                      setPageInput('')
                    }
                  }}
                >
                  <span className="text-muted-foreground">Go to</span>
                  <Input
                    type="number"
                    min={1}
                    max={meta.total_pages}
                    value={pageInput}
                    onChange={(e) => setPageInput(e.target.value)}
                    className="h-8 w-16"
                    placeholder={String(meta.current_page)}
                  />
                </form>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Order detail panel */}
      <Sheet open={!!drill} onOpenChange={(open) => !open && setDrill(null)}>
        <SheetContent size="default" aria-describedby={undefined} className="p-0">
          {drillLoading || !orderDetail ? (
            <>
              <SheetHeader>
                <SheetTitle>Order</SheetTitle>
              </SheetHeader>
              <SheetBody>
                <div className="py-10 text-center text-muted-foreground">
                  <Loader2 className="w-6 h-6 inline animate-spin" />
                </div>
              </SheetBody>
            </>
          ) : (
            <OrderPanel order={orderDetail} voids={orderVoids} formatCurrency={formatCurrency} boundaryHour={boundaryHour} />
          )}
        </SheetContent>
      </Sheet>
    </div>
  )
}

// ---------------------------------------------------------------------------

function Th({ children, className }: { children?: React.ReactNode; className?: string }) {
  return (
    <th className={cn('text-left p-3 text-xs font-medium text-muted-foreground', className)}>
      {children}
    </th>
  )
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs font-medium text-muted-foreground block mb-1.5">{label}</label>
      {children}
    </div>
  )
}

function SelfAuthorizedMark({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium mt-0.5',
        'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
        className,
      )}
      title="Voided and approved by the same person"
    >
      Self-authorized
    </span>
  )
}

function SummaryStrip({
  summary,
  loading,
  formatCurrency,
}: {
  summary: VoidLogSummary | undefined
  loading: boolean
  formatCurrency: (n: number) => string
}) {
  const tiles: { label: string; value: React.ReactNode; sub: React.ReactNode; tone?: 'danger' | 'warn' }[] = summary
    ? [
        {
          label: 'Voided value',
          value: compactCurrency(summary.total_value),
          sub: `${formatCurrency(summary.total_value)} across ${summary.count.toLocaleString()} item${summary.count === 1 ? '' : 's'}`,
          tone: 'danger',
        },
        {
          label: 'Most common reason',
          value: summary.top_reason?.reason ?? '—',
          sub: summary.top_reason
            ? `${summary.top_reason.count} of ${summary.count}${summary.other_count && summary.top_reason.reason !== 'Other' ? ` · Other: ${summary.other_count}` : ''}`
            : 'No voids in this period',
        },
        {
          label: 'Voided most by',
          value: summary.top_voider?.name ?? '—',
          sub: summary.top_voider
            ? `${summary.top_voider.count} void${summary.top_voider.count === 1 ? '' : 's'} · ${formatCurrency(summary.top_voider.value)}`
            : 'No voider recorded',
        },
        {
          label: 'Made, thrown away',
          value: (summary.wasted_count ?? 0).toLocaleString(),
          sub: (summary.wasted_count ?? 0) > 0
            ? `${formatCurrency(summary.waste_cost ?? 0)} of food · ${summary.not_made_count ?? 0} not made`
            : `${summary.not_made_count ?? 0} not made`,
          tone: (summary.wasted_count ?? 0) > 0 ? 'warn' : undefined,
        },
        {
          label: 'Self-authorized',
          value: summary.self_authorized_count.toLocaleString(),
          sub: 'voided and approved by the same person',
          tone: summary.self_authorized_count > 0 ? 'warn' : undefined,
        },
      ]
    : []

  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 rounded-lg border bg-muted/40 divide-y lg:divide-y-0 divide-border [&>*:nth-child(odd)]:border-r lg:[&>*:not(:last-child)]:border-r">
      {loading && !summary
        ? Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="px-4 py-3 space-y-2">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-6 w-28" />
              <Skeleton className="h-3 w-36" />
            </div>
          ))
        : tiles.map((t) => (
            <div key={t.label} className="px-4 py-3 min-w-0">
              <div className="text-xs text-muted-foreground">{t.label}</div>
              <div
                className={cn(
                  'text-lg font-semibold tracking-tight truncate tabular-nums',
                  t.tone === 'danger' && 'text-red-600',
                  t.tone === 'warn' && 'text-amber-700 dark:text-amber-400',
                )}
                title={typeof t.value === 'string' ? t.value : undefined}
              >
                {t.value}
              </div>
              <div className="text-xs text-muted-foreground truncate">{t.sub}</div>
            </div>
          ))}
    </div>
  )
}

function EmptyState({ hasActiveFilters, onClear }: { hasActiveFilters: boolean; onClear: () => void }) {
  return (
    <tr>
      <td colSpan={6} className="p-10">
        <div className="max-w-md mx-auto text-center">
          <div className="text-foreground font-medium">
            {hasActiveFilters ? 'No voids match your filters' : 'No voids in this period'}
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            {hasActiveFilters ? (
              <>Try a wider period or clear some filters.</>
            ) : (
              <>
                Voids appear here only after an item has been sent to the kitchen and a manager or admin
                authorizes the void with their PIN. Draft items removed before being fired are not tracked.
                Pick a different period above to look further back.
              </>
            )}
          </p>
          {hasActiveFilters && (
            <Button variant="outline" size="sm" className="mt-4" onClick={onClear}>
              Clear filters
            </Button>
          )}
        </div>
      </td>
    </tr>
  )
}

function OrderPanel({
  order,
  voids,
  formatCurrency,
  boundaryHour,
}: {
  order: Order
  voids: VoidLogEntry[]
  formatCurrency: (n: number) => string
  /** Tenant day_boundary_hour, so "n days after" counts business days (F-FE-N148). */
  boundaryHour: number
}) {
  const items = order.items || []
  const voidedIds = new Set(voids.map((v) => v.order_item_id).filter(Boolean))
  const voidedItemCount = items.filter((i) => i.status === 'voided' || voidedIds.has(i.id)).length
  const voidedTotal = voids.reduce((sum, v) => sum + voidValue(v), 0)
  const server = order.user ? `${order.user.first_name} ${order.user.last_name}` : null
  const payment =
    order.payments?.find((p) => p.status === 'completed')?.payment_method ??
    order.payments?.[0]?.payment_method ??
    order.checkout_payment_method ??
    null

  const headline = [
    orderTypeLabel(order.order_type),
    order.status.charAt(0).toUpperCase() + order.status.slice(1),
    formatDateTimeHuman(order.created_at),
  ].join(' · ')

  return (
    <>
      <SheetHeader className="pr-12">
        <SheetTitle>
          Order <span className="font-mono">{order.order_number}</span>
        </SheetTitle>
        <p className="text-sm text-muted-foreground">
          {headline}
          {server && (
            <>
              {' · served by '}
              <span className="text-foreground font-medium">{server}</span>
            </>
          )}
        </p>
      </SheetHeader>
      <SheetBody className="space-y-5">
        <section>
          <h4 className="text-xs font-medium text-muted-foreground mb-2">
            {voids.length === 1 ? 'What was voided' : `What was voided (${voids.length})`}
          </h4>
          {voids.length === 0 ? (
            <p className="text-sm text-muted-foreground">No voids recorded for this order.</p>
          ) : (
            <div className="space-y-2">
              {voids.map((v) => {
                const { category, note } = splitReason(v.reason)
                return (
                  <div
                    key={v.id}
                    className="rounded-lg border border-red-200 bg-red-50/60 dark:border-red-900/50 dark:bg-red-950/30 px-3.5 py-3 text-sm"
                  >
                    <div className="flex items-baseline justify-between gap-3 font-semibold">
                      <span>
                        {v.item_name} <span className="text-muted-foreground font-normal">×{v.quantity}</span>
                      </span>
                      <span className="text-red-600 tabular-nums">−{formatCurrency(voidValue(v))}</span>
                    </div>
                    <dl className="grid grid-cols-[4.5rem_1fr] gap-x-3 gap-y-1 mt-2.5 text-[13px]">
                      <dt className="text-muted-foreground">When</dt>
                      <dd>
                        {formatDateTimeHuman(v.created_at)}
                        <span className="text-muted-foreground"> · {daysAfterLabel(order.created_at, v.created_at, boundaryHour)}</span>
                      </dd>
                      <dt className="text-muted-foreground">Reason</dt>
                      <dd>
                        {category}
                        {note && <span className="text-muted-foreground"> — {note}</span>}
                      </dd>
                      <dt className="text-muted-foreground">By</dt>
                      <dd>
                        {v.voided_by_name || '—'}
                        {isSelfAuthorized(v) ? (
                          <SelfAuthorizedMark className="ml-2 mt-0 align-[1px]" />
                        ) : v.authorized_name ? (
                          <span className="text-muted-foreground"> · approved by {v.authorized_name}</span>
                        ) : null}
                      </dd>
                    </dl>
                  </div>
                )
              })}
            </div>
          )}
        </section>

        <section>
          <h4 className="text-xs font-medium text-muted-foreground mb-2">Order</h4>
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground">No item data available.</p>
          ) : (
            <div className="rounded-lg border overflow-hidden text-sm">
              <ul className="divide-y">
                {items.map((it) => {
                  const voided = it.status === 'voided' || voidedIds.has(it.id)
                  return (
                    <li key={it.id} className="flex items-center justify-between gap-3 px-3.5 py-2.5">
                      <span className={cn('font-medium', voided && 'text-muted-foreground line-through decoration-red-500')}>
                        {it.product?.name ?? 'Item'}
                        <span className="text-muted-foreground font-normal ml-1">×{it.quantity}</span>
                        {voided && (
                          <span className="ml-2 inline-block no-underline rounded bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300 px-1.5 py-0.5 text-[11px] font-medium [text-decoration:none]">
                            Voided
                          </span>
                        )}
                      </span>
                      <span className={cn('tabular-nums', voided && 'text-muted-foreground line-through')}>
                        {formatCurrency(it.total_price)}
                      </span>
                    </li>
                  )
                })}
              </ul>
              <div className="flex items-center justify-between px-3.5 py-2.5 bg-muted/50 font-semibold">
                <span className="text-muted-foreground font-medium">Paid</span>
                <span className="tabular-nums">{formatCurrency(order.total_amount)}</span>
              </div>
            </div>
          )}
        </section>

        <dl className="grid grid-cols-2 gap-x-5 text-[13px]">
          <Fact label="Table" value={order.table?.table_number ?? '—'} />
          <Fact label="Payment" value={payment ? payment.charAt(0).toUpperCase() + payment.slice(1) : '—'} />
          <Fact label="Voids on order" value={`${voidedItemCount} of ${items.length} item${items.length === 1 ? '' : 's'}`} />
          <Fact label="Voided value" value={formatCurrency(voidedTotal)} />
        </dl>
      </SheetBody>
    </>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-dashed">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium text-right">{value}</dd>
    </div>
  )
}

function outcomeFilterLabel(v: string): string {
  if (v === 'not_made' || v === 'wasted') return VOID_OUTCOME_LABEL[v]
  return 'Not recorded'
}

/** F-CTRL-N74: which kind of void it was. */
function OutcomePill({ outcome }: { outcome: 'not_made' | 'wasted' }) {
  return (
    <span
      className={cn(
        'mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        outcome === 'wasted'
          ? 'bg-warning-surface text-warning-fg border border-warning-border'
          : 'bg-muted text-muted-foreground',
      )}
    >
      {VOID_OUTCOME_LABEL[outcome]}
    </span>
  )
}
